/**
 * Tabellen-Stil-Vorlagen für das Creator Studio
 * Enthält 4 vordefinierte Stile mit CSS-in-JS Definitionen
 */

import type { TableElement } from './elementTypes';

/**
 * CSS-in-JS Style-Definition für Tabellen
 * Kann direkt in React-Komponenten verwendet werden
 */
export interface TableStyleDefinition {
  // Container-Styles
  container: React.CSSProperties;
  // Tabellen-Wrapper (für overflow)
  wrapper: React.CSSProperties;
  // Tabelle selbst
  table: React.CSSProperties;
  // Kopfzeilen-Zellen
  headerCell: React.CSSProperties;
  // Normale Zellen
  cell: React.CSSProperties;
  // Zeilen (für hover etc.)
  row: React.CSSProperties;
  // Alternierende Zeilen (für striped)
  rowAlt?: React.CSSProperties;
}

/**
 * Erweiterte Tabellen-Konfiguration mit Styles
 */
export interface ExtendedTableElement extends TableElement {
  styleDefinition: TableStyleDefinition;
  description: string;
}

/**
 * Gemeinsame Basis-Styles für alle Tabellen
 */
const baseStyles: Partial<TableStyleDefinition> = {
  container: {
    width: '100%',
    overflow: 'hidden',
  },
  wrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
};

/**
 * Minimal Style - Kein Border, dezente Trennlinien
 */
const minimalStyle: TableStyleDefinition = {
  container: {
    ...baseStyles.container,
    backgroundColor: 'transparent',
  },
  wrapper: {
    ...baseStyles.wrapper,
  },
  table: {
    ...baseStyles.table,
  },
  headerCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '2px solid #E5E7EB',
    backgroundColor: 'transparent',
  },
  cell: {
    padding: '12px 16px',
    textAlign: 'left',
    color: '#4B5563',
    borderBottom: '1px solid #F3F4F6',
  },
  row: {
    transition: 'background-color 0.15s ease',
  },
};

/**
 * Striped Style - Alternierende Zeilenfarben
 */
const stripedStyle: TableStyleDefinition = {
  container: {
    ...baseStyles.container,
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  wrapper: {
    ...baseStyles.wrapper,
  },
  table: {
    ...baseStyles.table,
  },
  headerCell: {
    padding: '14px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#111827',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
  },
  cell: {
    padding: '14px 16px',
    textAlign: 'left',
    color: '#374151',
  },
  row: {
    backgroundColor: '#FFFFFF',
  },
  rowAlt: {
    backgroundColor: '#F9FAFB',
  },
};

/**
 * Bordered Style - Vollständiges Grid mit Rahmen
 */
const borderedStyle: TableStyleDefinition = {
  container: {
    ...baseStyles.container,
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  wrapper: {
    ...baseStyles.wrapper,
  },
  table: {
    ...baseStyles.table,
    borderCollapse: 'collapse',
  },
  headerCell: {
    padding: '12px 14px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#1F2937',
    backgroundColor: '#F3F4F6',
    border: '1px solid #D1D5DB',
  },
  cell: {
    padding: '12px 14px',
    textAlign: 'left',
    color: '#374151',
    border: '1px solid #D1D5DB',
  },
  row: {},
};

/**
 * Modern Style - Abgerundete Ecken, Schatten, elegantes Design
 */
const modernStyle: TableStyleDefinition = {
  container: {
    ...baseStyles.container,
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
  },
  wrapper: {
    ...baseStyles.wrapper,
  },
  table: {
    ...baseStyles.table,
  },
  headerCell: {
    padding: '16px 20px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#1F2937',
    backgroundColor: '#F8FAFC',
    borderBottom: '2px solid #E2E8F0',
    letterSpacing: '0.025em',
    textTransform: 'uppercase',
    fontSize: '12px',
  },
  cell: {
    padding: '16px 20px',
    textAlign: 'left',
    color: '#475569',
    borderBottom: '1px solid #F1F5F9',
  },
  row: {
    transition: 'background-color 0.2s ease',
  },
};

/**
 * Alle Tabellen-Stil-Vorlagen
 */
export const TABLE_STYLES: ExtendedTableElement[] = [
  {
    id: 'table-minimal',
    name: 'Minimal',
    type: 'table',
    tags: ['minimal', 'clean', 'dezent', 'modern'],
    style: 'minimal',
    rows: 4,
    columns: 4,
    description: 'Dezentes Design ohne Rahmen, nur mit subtilen Trennlinien',
    defaultData: {
      headers: ['Name', 'Position', 'Abteilung', 'Standort'],
      rows: [
        ['Anna Müller', 'Entwicklerin', 'Engineering', 'Berlin'],
        ['Max Schmidt', 'Designer', 'Design', 'München'],
        ['Lisa Weber', 'PM', 'Produkt', 'Hamburg'],
      ],
    },
    styleDefinition: minimalStyle,
  },
  {
    id: 'table-striped',
    name: 'Gestreift',
    type: 'table',
    tags: ['gestreift', 'zebra', 'lesbar', 'klassisch'],
    style: 'striped',
    rows: 4,
    columns: 4,
    description: 'Alternierende Zeilenfarben für bessere Lesbarkeit',
    defaultData: {
      headers: ['Produkt', 'Preis', 'Menge', 'Gesamt'],
      rows: [
        ['Premium Plan', '€49.00', '2', '€98.00'],
        ['Add-On Service', '€19.00', '1', '€19.00'],
        ['Support-Paket', '€29.00', '1', '€29.00'],
      ],
    },
    styleDefinition: stripedStyle,
  },
  {
    id: 'table-bordered',
    name: 'Mit Rahmen',
    type: 'table',
    tags: ['rahmen', 'grid', 'tabelle', 'formell'],
    style: 'bordered',
    rows: 4,
    columns: 4,
    description: 'Vollständiges Grid mit sichtbaren Zellenrahmen',
    defaultData: {
      headers: ['KW', 'Montag', 'Dienstag', 'Mittwoch'],
      rows: [
        ['KW 1', 'Meeting', 'Workshop', 'Review'],
        ['KW 2', 'Sprint', 'Planning', 'Demo'],
        ['KW 3', 'Release', 'Testing', 'Deploy'],
      ],
    },
    styleDefinition: borderedStyle,
  },
  {
    id: 'table-modern',
    name: 'Modern',
    type: 'table',
    tags: ['modern', 'schatten', 'elegant', 'dashboard'],
    style: 'modern',
    rows: 4,
    columns: 4,
    description: 'Elegantes Design mit abgerundeten Ecken und Schatten',
    defaultData: {
      headers: ['Metrik', 'Aktuell', 'Ziel', 'Status'],
      rows: [
        ['Umsatz', '€125.4k', '€150k', '↗ +12%'],
        ['Nutzer', '8.2k', '10k', '↗ +8%'],
        ['Conversion', '3.2%', '4%', '↘ -0.3%'],
      ],
    },
    styleDefinition: modernStyle,
  },
];

/**
 * Tabellen-Stil nach ID abrufen
 */
export function getTableStyleById(id: string): ExtendedTableElement | undefined {
  return TABLE_STYLES.find((table) => table.id === id);
}

/**
 * Tabellen-Stil nach Style-Typ abrufen
 */
export function getTableByStyle(
  style: TableElement['style']
): ExtendedTableElement | undefined {
  return TABLE_STYLES.find((table) => table.style === style);
}

/**
 * Nur die Style-Definition abrufen
 */
export function getStyleDefinition(
  style: TableElement['style']
): TableStyleDefinition | undefined {
  const table = getTableByStyle(style);
  return table?.styleDefinition;
}

/**
 * Verfügbare Tabellen-Stile
 */
export const TABLE_STYLE_TYPES = ['minimal', 'striped', 'bordered', 'modern'] as const;
export type TableStyleType = (typeof TABLE_STYLE_TYPES)[number];

/**
 * Style-Namen für die UI
 */
export const TABLE_STYLE_LABELS: Record<TableStyleType, string> = {
  minimal: 'Minimal',
  striped: 'Gestreift',
  bordered: 'Mit Rahmen',
  modern: 'Modern',
};

/**
 * Hilfsfunktion: Ermittelt ob eine Zeile alternierend gefärbt werden soll
 */
export function isAlternatingRow(rowIndex: number): boolean {
  return rowIndex % 2 === 1;
}

/**
 * Hilfsfunktion: Gibt den richtigen Row-Style zurück
 */
export function getRowStyle(
  styleDefinition: TableStyleDefinition,
  rowIndex: number
): React.CSSProperties {
  if (styleDefinition.rowAlt && isAlternatingRow(rowIndex)) {
    return { ...styleDefinition.row, ...styleDefinition.rowAlt };
  }
  return styleDefinition.row;
}

/**
 * Leere Tabellendaten generieren
 */
export function createEmptyTableData(
  rows: number,
  columns: number
): { headers: string[]; rows: string[][] } {
  const headers = Array.from({ length: columns }, (_, i) => `Spalte ${i + 1}`);
  const dataRows = Array.from({ length: rows - 1 }, () =>
    Array.from({ length: columns }, () => '')
  );
  return { headers, rows: dataRows };
}
